/**
 * Cross-cutting signal for "the server says this organization's subscription is locked".
 *
 * The lock screen is normally driven by GET /api/subscription. If that request is the one
 * failing, the UI would otherwise render the full app while every other call 403s. The API
 * error interceptor sets this flag when it sees a `subscription_locked` denial, so the lock
 * screen appears from the denials themselves without waiting for the subscription endpoint
 * to recover.
 *
 * Cleared as soon as a subscription fetch comes back in any non-locked state.
 */

/**
 * The organization a lock was observed for, or null. Scoped by organization rather than a
 * plain boolean: a user can belong to several organizations, and a 403 that arrives late from
 * a previous organization must not flash the lock screen over the one now selected.
 */
let lockedOrgId: string | null = null;
const listeners = new Set<() => void>();

function emit() {
    for (const listener of listeners) listener();
}

/** Deny reasons returned by DispatchAuthorization via the gateway's 403 body. */
export const DENY_SUBSCRIPTION_LOCKED = 'subscription_locked';
export const DENY_SUBSCRIPTION_READONLY = 'subscription_readonly';

/** Records (orgId) or clears (null) the organization the server reported as locked. */
export function setServerLockedOrg(orgId: string | null) {
    if (lockedOrgId === orgId) return;
    lockedOrgId = orgId;
    emit();
}

export function subscribeToServerLocked(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

export function getServerLockedOrg() {
    return lockedOrgId;
}
